'use client'

import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { Card } from '@/entities/auth'
import { useSignUpGuestStore, SignUpGuestButton } from '@/features/sign-up'
import {
  PASSPORT_VERIFICATION_DIALOG,
  PassportDialog,
  type PhoneVerificationResult,
  type TPassportVerificationForm,
} from '@/features/verify-identity'
import {
  Slot,
  useDialogState,
  WithProvider,
  DialogProvider,
} from '@/shared/lib'
import { PassportVerification, PhoneVerification } from '@/widgets/verification'

import { VerificationStatus as Status } from '../../model/verification'

const titleMap = {
  [Status.READY]: '본인 인증을 진행해 주세요',
  [Status.VERIFIED_PHONE]: '본인 인증이 완료되었습니다',
  [Status.VERIFIED_PASSPORT]: '여권 사진 등록이 완료되었습니다',
}

const Verification = () => {
  const [status, setStatus] = useState<Status>(Status.READY)

  const { isOpen, close, closeHandler } = useDialogState()

  const { actions, verification } = useSignUpGuestStore()

  const showDescription = status === Status.VERIFIED_PASSPORT
  const showPhoneVerification = status !== Status.VERIFIED_PASSPORT
  const showPassportVerification = status !== Status.VERIFIED_PHONE

  const disabled = status === Status.READY

  const handleSuccessPhoneVerification = ({
    certNum,
    phoneNumber,
  }: PhoneVerificationResult) => {
    actions.updateVerification({ phoneNumber, certNum })
    setStatus(Status.VERIFIED_PHONE)

    toast('본인인증이 완료되었습니다.')
  }

  const handleSuccessPassportVerification = (
    formValues: TPassportVerificationForm,
  ) => {
    close(PASSPORT_VERIFICATION_DIALOG)

    actions.updateVerification(formValues)
    setStatus(Status.VERIFIED_PASSPORT)

    toast('여권 사진 등록이 완료되었습니다.')
  }

  useEffect(() => {
    actions.updateVerification({
      phoneNumber: '',
      certNum: '',
      contactNumber: '',
      passportImageUrl: '',
    })
  }, [])

  return (
    <Card progress={75}>
      <Slot name="title">{titleMap[status]}</Slot>
      {showDescription && (
        <Slot name="description">심사에는 최대 1일이 소요됩니다.</Slot>
      )}
      <Slot name="children">
        <div className="space-y-15 text-center">
          {showPhoneVerification && (
            <PhoneVerification
              isVerified={status === Status.VERIFIED_PHONE}
              onSuccess={handleSuccessPhoneVerification}
            />
          )}

          {showPassportVerification && (
            <PassportVerification
              isVerified={status === Status.VERIFIED_PASSPORT}
            />
          )}

          {isOpen(PASSPORT_VERIFICATION_DIALOG) && (
            <PassportDialog
              isOpen={isOpen(PASSPORT_VERIFICATION_DIALOG)}
              onClose={closeHandler(PASSPORT_VERIFICATION_DIALOG)}
              onSuccess={handleSuccessPassportVerification}
              defaultValues={{
                contactNumber: verification?.contactNumber,
                passportImageUrl: verification?.passportImageUrl,
              }}
            />
          )}

          <SignUpGuestButton disabled={disabled} />
        </div>
      </Slot>
    </Card>
  )
}

export const SignUpGuestVerification = WithProvider({
  Component: Verification,
  Provider: DialogProvider,
})
